// Edit a station (spec/13 §12, F-ORG-03): a single short form in a centred dialog — number, name, class, zone,
// seats and availability. A station's `kind` is fixed once defined (a machine that becomes a different
// kind is a different station), so it is shown but not editable. Only the fields that changed are sent
// (the contract's PATCH is `.partial()`), and taking a station out of service clears its reason when it
// is put back. `stations:manage` is enforced again by the server on every request.

import type { Station, StationStatus, UpdateStation } from '@clavian/api-client';
import { MAX_STATION_CAPACITY } from '@clavian/contracts/constants';
import { useTranslation } from '@clavian/i18n/react';
import { Button, Dialog, FormSection, InfoTip, SelectField, Switch, TextField, useToast } from '@clavian/ui-web';
import { useState, type FormEvent, type ReactNode } from 'react';

import { PROBLEM, problemCode } from '../api/problems';
import { useProblem } from '../api/use-problem';
import { Notice } from '../ui';
import { useStationClasses, useUpdateStation, useZones } from './queries';

interface FormState {
  readonly number: string;
  readonly name: string;
  readonly classId: string;
  readonly zoneId: string;
  readonly capacity: string;
  readonly status: StationStatus;
  readonly disabledReason: string;
  /** Run by staff, with no app (ADR-0033) — the inverse of `managed`. */
  readonly staffRun: boolean;
}

function formFor(station: Station): FormState {
  return {
    number: String(station.number),
    name: station.name ?? '',
    classId: station.classId,
    zoneId: station.zoneId ?? '',
    capacity: String(station.capacity),
    status: station.status,
    disabledReason: station.disabledReason ?? '',
    staffRun: station.managed === false,
  };
}

export function EditStationDialog({
  station,
  onClose,
}: {
  readonly station: Station | null;
  readonly onClose: () => void;
}): ReactNode {
  const { t } = useTranslation('console');
  return (
    <Dialog
      open={station !== null}
      onOpenChange={(open) => (open ? undefined : onClose())}
      size="md"
      dirty="auto"
      eyebrow={station === null ? undefined : (station.name ?? t('floor.tile.numbered', { number: station.number }))}
      title={t('stations.edit.title')}
      description={t('stations.edit.intro')}
    >
      {/* Remounted per station, so the form always starts from the station being edited. */}
      {station === null ? null : <EditStationForm station={station} onDone={onClose} />}
    </Dialog>
  );
}

interface FieldErrors {
  number?: string;
  classId?: string;
  zoneId?: string;
  capacity?: string;
  form?: string;
}

function EditStationForm({ station, onDone }: { readonly station: Station; readonly onDone: () => void }): ReactNode {
  const { t } = useTranslation('console');
  const problem = useProblem();
  const { show } = useToast();
  const classes = useStationClasses(station.organizationId, station.venueId);
  const zones = useZones(station.organizationId, station.venueId);
  const update = useUpdateStation(station.organizationId, station.venueId);
  const [form, setForm] = useState<FormState>(() => formFor(station));
  const [errors, setErrors] = useState<FieldErrors>({});

  const classOptions = (classes.data ?? []).map((klass) => ({ value: klass.id, label: klass.name }));
  const zoneOptions = [{ value: '', label: t('stations.edit.zoneNone') }, ...(zones.data ?? []).map((zone) => ({ value: zone.id, label: zone.name }))];
  const statusOptions: readonly { value: StationStatus; label: string }[] = [
    { value: 'active', label: t('stations.edit.statusActive') },
    { value: 'disabled', label: t('stations.edit.statusDisabled') },
  ];

  /** The fields that differ from the station as it stands, as an `UpdateStation` patch. */
  function diff(): UpdateStation {
    const patch: UpdateStation = {};
    const number = Number(form.number);
    if (Number.isInteger(number) && number !== station.number) patch.number = number;
    const name = form.name.trim() === '' ? null : form.name.trim();
    if (name !== station.name) patch.name = name;
    if (form.classId !== station.classId) patch.classId = form.classId;
    const zoneId = form.zoneId === '' ? null : form.zoneId;
    if (zoneId !== station.zoneId) patch.zoneId = zoneId;
    const capacity = Number(form.capacity);
    if (Number.isInteger(capacity) && capacity !== station.capacity) patch.capacity = capacity;
    if (form.status !== station.status) patch.status = form.status;
    // An active station carries no reason; a disabled one takes the typed reason (empty is null).
    const reason = form.status === 'disabled' ? (form.disabledReason.trim() === '' ? null : form.disabledReason.trim()) : null;
    if (reason !== station.disabledReason) patch.disabledReason = reason;
    if (form.staffRun !== (station.managed === false)) patch.managed = !form.staffRun;
    return patch;
  }

  function submit(event: FormEvent<HTMLFormElement>): void {
    event.preventDefault();
    const next: FieldErrors = {};
    const number = Number(form.number);
    if (form.number.trim() === '' || !Number.isInteger(number) || number < 1) next.number = t('stations.edit.required');
    if (form.classId === '') next.classId = t('stations.edit.required');
    const capacity = Number(form.capacity);
    if (form.capacity.trim() === '' || !Number.isInteger(capacity) || capacity < 1 || capacity > MAX_STATION_CAPACITY) {
      next.capacity = t('stations.edit.capacityRange', { max: MAX_STATION_CAPACITY });
    }
    if (Object.keys(next).length > 0) {
      setErrors(next);
      return;
    }

    const patch = diff();
    if (Object.keys(patch).length === 0) {
      setErrors({ form: t('stations.edit.noChanges') });
      return;
    }
    setErrors({});

    update.mutate(
      { stationId: station.id, body: patch },
      {
        onSuccess: (updated) => {
          show({ tone: 'ok', title: t('stations.edit.saved', { number: updated.number }) });
          onDone();
        },
        onError: (error) => {
          const code = problemCode(error);
          // Turning a paired station staff-run is refused while its device is bound (ADR-0033).
          if (code === PROBLEM.conflict && form.staffRun && station.managed !== false && station.deviceId !== null) setErrors({ form: t('stations.staffRun.pairedConflict') });
          else if (code === PROBLEM.conflict) setErrors({ number: t('stations.edit.numberTaken') });
          else if (problem.fieldMessage(error, 'number') !== undefined) setErrors({ number: problem.fieldMessage(error, 'number') });
          else setErrors({ form: problem.text(error, { fallback: t('stations.edit.refused') }) });
        },
      },
    );
  }

  return (
    <form className="console-form" aria-label={t('stations.edit.title')} noValidate onSubmit={submit}>
      {errors.form !== undefined ? (
        <Notice tone="danger" role="alert">
          {errors.form}
        </Notice>
      ) : null}
      {/* Four fields to edit a PC; its capacity, its status and who runs it wait behind More options. */}
      <FormSection
        forceOpen={errors.capacity !== undefined}
        more={
          <>
      <div className="console-form__row">
        <TextField
          label={t('stations.edit.capacity')}
          description={t('stations.edit.capacityHint', { max: MAX_STATION_CAPACITY })}
          required
          numeric
          inputMode="numeric"
          value={form.capacity}
          onChange={(event) => setForm((current) => ({ ...current, capacity: event.target.value }))}
          error={errors.capacity}
        />
        <SelectField
          label={t('stations.edit.status')}
          description={t('stations.edit.statusHint')}
          options={statusOptions}
          value={form.status}
          onValueChange={(value) => setForm((current) => ({ ...current, status: value as StationStatus }))}
        />
      </div>
      {form.status === 'disabled' ? (
        <TextField
          label={t('stations.edit.disabledReason')}
          description={t('stations.edit.disabledReasonHint')}
          showOptional
          value={form.disabledReason}
          onChange={(event) => setForm((current) => ({ ...current, disabledReason: event.target.value }))}
        />
      ) : null}
      {station.role === 'counter' ? null : (
        <div className="console-switch-tip">
          <Switch
            label={t('stations.staffRun.editLabel')}
            checked={form.staffRun}
            onCheckedChange={(checked) => setForm((current) => ({ ...current, staffRun: checked }))}
          />
          <InfoTip>{t('stations.staffRun.editHint')}</InfoTip>
        </div>
      )}
          </>
        }
      >
      <div className="console-form__row">
        <TextField
          label={t('stations.edit.number')}
          description={t('stations.edit.numberHint')}
          required
          numeric
          inputMode="numeric"
          value={form.number}
          onChange={(event) => setForm((current) => ({ ...current, number: event.target.value }))}
          error={errors.number}
        />
        <TextField
          label={t('stations.edit.name')}
          description={t('stations.edit.nameHint')}
          showOptional
          value={form.name}
          onChange={(event) => setForm((current) => ({ ...current, name: event.target.value }))}
        />
      </div>
      <div className="console-form__row">
        <SelectField
          label={t('stations.edit.class')}
          required
          placeholder={t('stations.edit.chooseClass')}
          options={classOptions}
          value={form.classId}
          onValueChange={(value) => setForm((current) => ({ ...current, classId: value }))}
          error={errors.classId}
        />
        <SelectField
          label={t('stations.edit.zone')}
          showOptional
          options={zoneOptions}
          value={form.zoneId}
          onValueChange={(value) => setForm((current) => ({ ...current, zoneId: value }))}
        />
      </div>
      </FormSection>
      <div className="console-form__actions console-form__actions--end">
        <Button type="button" variant="ghost" onClick={onDone}>
          {t('common.cancel')}
        </Button>
        <Button type="submit" variant="primary" busy={update.isPending}>
          {t('stations.edit.submit')}
        </Button>
      </div>
    </form>
  );
}
